import { MutableRefObject, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
  pluckAttributesFromFormData,
} from 'dashboard/forms/FormNewDataSource';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import {
  DataSourceStatus,
  DataSourceUniqueStatus,
  ExternalIntegration,
  ExternalIntegrationDataSource,
  ExternalIntegrationDataSourceGoogleSheets,
  S3Object,
} from 'dashboard/models/external-integration';
import { ImporterArchiveAfterCompletion } from 'dashboard/models/importer';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { setExternalIntegration } from 'dashboard/redux/actions/external-integrations';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { refreshDataSource } from 'dashboard/redux/thunk-actions/data-source';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import {
  createImporter,
  loadImporter,
  loadImportersList,
  updateImporter,
} from 'dashboard/redux/thunk-actions/importers';
import { loadS3Config } from 'dashboard/redux/thunk-actions/s3';
import DashboardImporterStepperView, {
  NewImporterFormData,
} from 'dashboard/views/DashboardImporterStepperView/DashboardImporterStepperView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import {
  ApiEndpoint,
  ApiModel,
  ExternalIntegrationDataSource as ApiModelExternalIntegrationDataSource,
} from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type PropsFromRedux = ConnectedProps<typeof connector>;
type DashboardImporterStepperRouteProps = PropsFromRedux;

type Params = { importerId: string };

const DashboardImporterStepperRoute = (props: DashboardImporterStepperRouteProps) => {
  const navigate = useNavigate();
  const { importerId } = useParams<Params>() as Params; // importerId will always be defined

  const hasTransactionsEnabled = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.TransactionsDashboard,
    props.currentEnvironment.type,
  );

  const { loading: s3ConfigLoading } = useApiLoader({
    loader: async () => {
      await props.loadS3Config();
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load S3 config, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadS3Config, props.showMessage],
  });

  const addDataSource = async (values: FormNewDataSourceData): Promise<AddDataSourceResponse> => {
    try {
      const attributes = pluckAttributesFromFormData(values);

      const response = await SessionClientDashboard.postResourceCreate<ExternalIntegration>(
        {
          attributes,
          type: values.type,
        },
        { contentType: 'multipart/form-data' },
      );

      props.setExternalIntegration(response.data);
      props.showMessage('Successfully added data source', MessageType.Success);
      return response.data;
    } catch (err) {
      if (err instanceof ApiResponseError && isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err instanceof ApiResponseError && err.status === 400) {
        // If inputs are invalid, parse the error message and propogate it back to the form
        if (err.errors && err.errors[0]?.title === 'Bad request' && err.errors[0].details) {
          return err.errors[0].details.toString();
        } else {
          props.showMessage('Bad Request. Unable to Parse Error.', MessageType.Failure);
        }
      } else {
        props.showMessage('Please try again.', MessageType.Failure);
      }
      return null;
    }
  };

  const { error: refreshDataSourceError, callback: handleRefreshDataSource } = useApiCallback(
    async (dataSource: ExternalIntegrationDataSource) => {
      await props.refreshDataSource(dataSource);
    },
    [props.refreshDataSource, props.showMessage],
  );

  useApiErrorHandler({
    error: refreshDataSourceError,
    onError: () => props.showMessage('Failed to refresh data source.', MessageType.Failure),
    onNetworkError: () => props.showMessage('Failed to refresh data source.', MessageType.Failure),
  });

  const getAllGoogleSheets = async (
    code: string,
    redirectUrl: string,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ): Promise<GoogleSheetsFilesResponse> => {
    try {
      const attributes = { code, redirectUrl };

      const response = await SessionClientDashboard.postResourceSingletonActionNew<
        ExternalIntegrationDataSourceGoogleSheets,
        {
          type: ApiModel.ExternalIntegrationDataSourceGoogleSheets;
          attributes: { code: string; redirectUrl: string };
        }
      >(ApiEndpoint.ExternalIntegrationDataSource, 'schema', {
        attributes,
        type: ApiModel.ExternalIntegrationDataSourceGoogleSheets,
      });
      props.showMessage('Successfully authorized to Google Sheets.', MessageType.Success);

      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        token: response.meta.token,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        files: response.meta.files,
      };
    } catch (err) {
      if (err instanceof ApiResponseError && isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    }
  };

  const handleListS3Objects = async (values: FormNewDataSourceDataS3) => {
    const res = await SessionClientDashboard.getEndpoint<{ objects: S3Object[] }>(
      ApiEndpoint.DataSourceS3ListObjects,
      {
        filter: {
          arn: values.credentials.arn,
          external_id: values.credentials.externalId,
          region: values.region,
          bucket: values.bucket,
          prefix: values.csvPathPrefix,
        },
      },
    );
    return res.objects;
  };

  const { loading: isExternalIntegrationsLoading } = useApiLoader({
    loader: async () => {
      await props.loadExternalIntegrations();
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load data sources, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadExternalIntegrations, props.showMessage],
  });

  const { loading: isImportersLoading } = useApiLoader({
    loader: async () => {
      await props.loadImportersList(props.currentEnvironment);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importers, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.currentEnvironment, props.loadImportersList, props.showMessage],
  });

  const { loading: isLoadingAccountTypes } = useApiLoader({
    loader: () => props.loadAccountTypes(),
    deps: [props.currentEnvironment],
    skip: false,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account types.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    isLoading: isLoadingTransactionTypes,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({ filter: { status: [TransactionTypeStatus.Active] } });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  const addImporter = async (values: NewImporterFormData): Promise<void> => {
    try {
      const response = await props.createImporter(
        values.name.trim(),
        values.type,
        values.dataSourceId,
        {
          destination: values.destination,
          'destination-type-id': values.destinationTypeId,
          'table-name': values['table-name'],
          'sync-mode': values.syncMode,
          'array-sync-mode': values.arraySyncMode,
          'archive-after-completion':
            values.archiveAfterCompletion === ImporterArchiveAfterCompletion.True,
        },
      );

      navigate(`/dashboard/imports/importers/${response.data.id}/data-mapping`);
    } catch (err) {
      if (err instanceof ApiResponseError && isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err instanceof ApiResponseError && (err.status === 422 || err.status === 409)) {
        props.showMessage(
          'There is already an active importer with this name.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    }
  };

  const updateImporter = async (values: NewImporterFormData): Promise<void> => {
    try {
      await props.updateImporter(
        importerId,
        values.type,
        values.name.trim(),
        {
          destination: values.destination,
          'destination-type-id': values.destinationTypeId,
          'table-name': values['table-name'],
          'sync-mode': values.syncMode,
          'array-sync-mode': values.arraySyncMode,
          'archive-after-completion':
            values.archiveAfterCompletion === ImporterArchiveAfterCompletion.True,
        },
        values.dataSourceId,
      );

      navigate(`/dashboard/imports/importers/${importerId}/data-mapping`);
    } catch (err) {
      props.showMessage('Please try again.', MessageType.Failure);
    }
  };

  const timeoutInterval = 2000;
  const timer: MutableRefObject<ReturnType<typeof setInterval> | null> = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  useEffect(() => {
    if (isExternalIntegrationsLoading) return;
    const dataSources: ExternalIntegrationDataSource[] =
      props.externalIntegrations.filter(isDataSource);
    const isDataSourceProcessing = dataSources.some(
      (ds) => (ds.attributes.status as DataSourceStatus) === DataSourceUniqueStatus.Processing,
    );

    if (isDataSourceProcessing) {
      // start polling if not already
      if (!timer.current) {
        timer.current = setInterval(async () => {
          await props.loadExternalIntegrations();
        }, timeoutInterval);
      }
    }
    // stop polling if no longer need to poll
    else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }

    // clear the timer upon unmount
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isExternalIntegrationsLoading, props.externalIntegrations]);

  // in case we need to edit an existing importer
  const importer = importerId ? props.importers.find((i) => i.id === importerId) : undefined;

  const isDataSource = (e: ExternalIntegration): e is ExternalIntegrationDataSource => {
    return ApiModelExternalIntegrationDataSource.includes(e.type);
  };
  const dataSources = props.externalIntegrations.filter(isDataSource);

  if (
    !dataSources ||
    isExternalIntegrationsLoading ||
    isLoadingAccountTypes ||
    isImportersLoading ||
    isLoadingTransactionTypes ||
    s3ConfigLoading ||
    (importerId && !importer)
  ) {
    // We check if `importer` was found, because isLoading does not default to `true` at first render
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    return (
      <div>
        <DashboardImporterStepperView
          updateImporter={updateImporter}
          accountTypes={props.accountTypes}
          addImporter={addImporter}
          dataSources={dataSources}
          hasTransactionsEnabled={hasTransactionsEnabled}
          currentImporterId={importerId}
          importers={props.importers}
          txnTypes={transactionTypes}
          addDataSource={addDataSource}
          refreshDataSource={handleRefreshDataSource}
          getAllGoogleSheets={getAllGoogleSheets}
          s3Config={props.s3Config}
          handleListS3Objects={handleListS3Objects}
        />
      </div>
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
    importers: state.importers.importers,
    organization: state.organization.organization,
    s3Config: state.s3.s3Config,
  };
};

const mapDispatchToProps = {
  createImporter,
  loadExternalIntegrations,
  loadImporter,
  loadImportersList,
  loadAccountTypes,
  loadS3Config,
  refreshDataSource,
  setExternalIntegration,
  showMessage,
  updateImporter,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(DashboardImporterStepperRoute);
